import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { loadSurveysWithStats } from "../../redux/slices/belediyeSurveyReportsSlice";

// Başvurular ile AYNI başlık/araç-çubuğu/sekme/sayfalama mimarisi
import "../ApplicationsPage.css";
import "../ApplicationsPage.mobile.css";
import "./SurveyReportsPage.css";

import PageHeader from "../../components/utils/PageHeader";
import SearchBox from "../../components/utils/SearchBox";
import FilterSheet from "../../components/utils/FilterSheet";
import Pagination from "../../components/utils/Pagination";

import {
  ClipboardDocumentCheckIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  CircleStackIcon,
  CheckCircleIcon,
  XCircleIcon,
  Squares2X2Icon,
} from "@heroicons/react/24/outline";

const PAGE_SIZE = 50;

// Tümü / Aktif / Pasif — Başvurular sekmeleriyle AYNI çip yapısı
const STATUS_TABS = [
  { key: "all", label: "Tümü", icon: Squares2X2Icon },
  { key: "active", label: "Aktif", icon: CheckCircleIcon },
  { key: "passive", label: "Pasif", icon: XCircleIcon },
];

const trLower = (v) => String(v ?? "").toLocaleLowerCase("tr");

const fmtDateTime = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return new Intl.DateTimeFormat("tr-TR", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(d);
  } catch {
    return String(iso);
  }
};

// last_response_at (ISO) → YYYY-MM-DD (tarih karşılaştırması için)
const isoDatePart = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  } catch {
    return null;
  }
};

export default function SurveyReportsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { surveys, surveysLoading, surveysError } = useSelector(
    (s) => s.belediyeSurveyReports
  );

  // ── UI state (client-side arama/filtre/sekme/sayfa) ──────────
  const [activeTab, setActiveTab] = useState("all");
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);

  const surveysErrorText = useMemo(() => {
    if (!surveysError) return null;
    if (typeof surveysError === "string") return surveysError;
    if (typeof surveysError === "object") {
      const msg = surveysError.message || "Anket listesi alınamadı.";
      const status = surveysError.status ? ` (HTTP ${surveysError.status})` : "";
      return `${msg}${status}`;
    }
    return String(surveysError);
  }, [surveysError]);

  const normalizedSurveys = useMemo(() => {
    if (Array.isArray(surveys)) return surveys;
    if (surveys && typeof surveys === "object") {
      if (Array.isArray(surveys.results)) return surveys.results;
      if (Array.isArray(surveys.items)) return surveys.items;
    }
    return [];
  }, [surveys]);

  useEffect(() => {
    dispatch(loadSurveysWithStats());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // ── Arama + tarih ile daralt (sekme sayıları bunun üzerinden) ─
  const baseFiltered = useMemo(() => {
    const needle = trLower(q).trim();
    return normalizedSurveys.filter((row) => {
      if (needle) {
        const hay = `${trLower(row.name)} v${row.version}`;
        if (!hay.includes(needle)) return false;
      }
      if (dateFrom || dateTo) {
        const d = isoDatePart(row.last_response_at);
        if (!d) return false; // yanıt tarihi yoksa tarih filtresinde eleme
        if (dateFrom && d < dateFrom) return false;
        if (dateTo && d > dateTo) return false;
      }
      return true;
    });
  }, [normalizedSurveys, q, dateFrom, dateTo]);

  const counts = useMemo(() => {
    let active = 0;
    for (const r of baseFiltered) if (r.is_active) active += 1;
    return {
      all: baseFiltered.length,
      active,
      passive: baseFiltered.length - active,
    };
  }, [baseFiltered]);

  const filtered = useMemo(() => {
    if (activeTab === "active") return baseFiltered.filter((r) => r.is_active);
    if (activeTab === "passive") return baseFiltered.filter((r) => !r.is_active);
    return baseFiltered;
  }, [baseFiltered, activeTab]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);
  const paged = useMemo(
    () => filtered.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE),
    [filtered, pageSafe]
  );

  // Filtre/arama/sekme değişince sayfa 1'e dön
  useEffect(() => {
    setPage(1);
  }, [q, dateFrom, dateTo, activeTab]);

  const activeFilterCount = (dateFrom ? 1 : 0) + (dateTo ? 1 : 0);
  const clearFilters = () => {
    setDateFrom("");
    setDateTo("");
  };

  const onOpen = (id) => {
    if (!id) return;
    navigate(`/yonetim/anketler/${id}`); // FAZ-22 4b: canonical
  };

  const infoContent = (
    <>
      <ul>
        <li>
          Sekmelerle durumları süzün: <b>Tümü</b> / <b>Aktif</b> / <b>Pasif</b>.
          Sekme adının yanındaki sayı, o durumdaki anket sayısıdır.
        </li>
        <li>
          <b>Arama</b> anket adına göre çalışır (sürüm dahil).
        </li>
        <li>
          <b>Filtreler</b> ile <b>son yanıt tarihi</b> aralığına göre daraltın;
          filtre tüm sekmeleri kapsar, sekme sayıları filtreye göre güncellenir.
        </li>
        <li>
          Bir satıra tıklayın: anketin <b>rapor detayına</b> (özet, zaman serisi,
          sorular) gidersiniz.
        </li>
      </ul>
    </>
  );

  const isFiltered = !!(q || dateFrom || dateTo || activeTab !== "all");

  return (
    <div className="applications-page survey-reports-page">
      {/* STICKY: HEADER + TOOLBAR — paylaşılan PageHeader */}
      <PageHeader
        title="Anketler"
        infoTitle="Anketler — Bilgi"
        infoContent={infoContent}
        tabs={
          <div
            className="applications-tabs"
            role="tablist"
            aria-label="Anket durumları"
          >
            {STATUS_TABS.map((tab) => {
              const active = tab.key === activeTab;
              const TabIcon = tab.icon;
              return (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  className={`applications-tab${active ? " active" : ""}`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  <TabIcon className="applications-tab-icon" />
                  <span className="applications-tab-label">{tab.label}</span>
                  <span className="applications-tab-count">
                    {counts[tab.key]}
                  </span>
                </button>
              );
            })}
          </div>
        }
      >
        <SearchBox value={q} onChange={setQ} placeholder="Ara — anket adı" />

        <FilterSheet
          activeCount={activeFilterCount}
          onClear={clearFilters}
          title="Filtreler"
          label="Filtreler"
        >
          <div className="afp-section">
            <div className="afp-label">Son yanıt tarihi</div>
            <div className="afp-dates">
              <input
                type="date"
                className="md-input"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
              <span className="afp-dates-sep">–</span>
              <input
                type="date"
                className="md-input"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
          </div>
        </FilterSheet>
      </PageHeader>

      {/* LIST */}
      <div className="applications-list-shell">
        {/* LOADING */}
        {surveysLoading ? (
          <div className="sr-state">
            <div className="sr-state-line">
              <span className="sr-dot" />
              <span>Yükleniyor…</span>
            </div>
            <div className="sr-state-sub">Anket rapor özeti getiriliyor.</div>
          </div>
        ) : null}

        {/* ERROR */}
        {!surveysLoading && surveysErrorText ? (
          <div className="sr-state sr-state-error">
            <div className="sr-state-line">
              <ExclamationTriangleIcon className="sr-state-ico sr-ico-danger" />
              <span>Hata</span>
            </div>
            <div className="sr-state-sub">{surveysErrorText}</div>
          </div>
        ) : null}

        {/* EMPTY */}
        {!surveysLoading && !surveysErrorText && filtered.length === 0 ? (
          <div className="sr-state">
            <div className="sr-state-line">
              <CircleStackIcon className="sr-state-ico" />
              <span>Kayıt bulunamadı</span>
            </div>
            <div className="sr-state-sub">
              {isFiltered
                ? "Arama/filtre kriterlerine uyan anket yok. Filtreleri değiştirin."
                : "Henüz anket kaydı yok."}
            </div>
          </div>
        ) : null}

        {/* ROWS */}
        {!surveysLoading && !surveysErrorText && filtered.length > 0 ? (
          <div className="sr-list" role="list">
            {paged.map((row) => {
              const last = fmtDateTime(row.last_response_at);
              const active = !!row.is_active;

              return (
                <button
                  key={row.id}
                  type="button"
                  className="sr-row"
                  role="listitem"
                  onClick={() => onOpen(row.id)}
                >
                  <ClipboardDocumentCheckIcon className="sr-row-ico" />

                  <div className="sr-row-main">
                    <div className="sr-row-title">
                      <div className="sr-name">{row.name}</div>
                      <div className="sr-version">v{row.version}</div>
                    </div>

                    <div className="sr-row-meta">
                      <span className="sr-meta-item">
                        Son yanıt: <b>{last || "-"}</b>
                      </span>
                    </div>
                  </div>

                  <div className="sr-row-right">
                    <span
                      className={`sr-chip ${
                        active ? "sr-chip-success" : "sr-chip-danger"
                      }`}
                      title={active ? "Aktif" : "Pasif"}
                    >
                      {active ? (
                        <CheckCircleIcon className="sr-chip-ico" />
                      ) : (
                        <XCircleIcon className="sr-chip-ico" />
                      )}
                      {active ? "Aktif" : "Pasif"}
                    </span>

                    <span className="sr-pill" title="Toplam yanıt">
                      <b>{row.responses ?? 0}</b> yanıt
                    </span>

                    <ChevronRightIcon className="sr-row-chevron" />
                  </div>
                </button>
              );
            })}
          </div>
        ) : null}
      </div>

      {/* PAGINATION — paylaşılan bileşen */}
      {!surveysLoading && !surveysErrorText && filtered.length > 0 ? (
        <Pagination
          page={pageSafe}
          totalCount={filtered.length}
          pageSize={PAGE_SIZE}
          shownCount={paged.length}
          onChange={setPage}
        />
      ) : null}
    </div>
  );
}
